import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, CalendarClock } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACT } from "@/lib/data";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Services" },
  { to: "/engagements", label: "Engagements" },
  { to: "/villes", label: "Villes" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl transition-all duration-300 ${
        scrolled
          ? "bg-white/90 border-b border-gold/20 shadow-[0_1px_0_0_rgba(194,164,111,0.15)]"
          : "bg-white/70 border-b border-transparent"
      }`}
    >
      <div className="container-tm flex items-center justify-between h-[88px]">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3"
          aria-label="Terre et Mer Conciergerie - Accueil"
        >
          <Logo variant="dark" className="h-14 sm:h-14 md:h-16 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-10" data-testid="primary-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.22em] font-medium transition-colors duration-200 ${
                  isActive ? "text-sage" : "text-ink hover:text-sage"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            state={{ scrollTo: "simulator" }}
            data-testid="header-cta-estimate"
            className="btn-primary"
          >
            Calculez vos revenus
          </Link>
        </div>

        <button
          aria-label="Menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-ink hover:text-sage transition-colors"
        >
          {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-gold/20" data-testid="mobile-menu">
          <div className="container-tm py-6 flex flex-col gap-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] font-medium py-2 ${
                    isActive ? "text-sage" : "text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" state={{ scrollTo: "simulator" }} className="btn-primary mt-2 w-full" data-testid="mobile-cta-estimate">
              Calculez vos revenus
            </Link>
            <a
              href={CONTACT.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-cta-calendly"
              className="btn-accent w-full"
            >
              <CalendarClock size={14} strokeWidth={2} className="mr-2" />
              Prendre RDV 15 min
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
